import React, { useEffect, useRef, useState } from 'react';
import { Template1ResumeData, flattenSkills, filterNullEntries } from './Template1';
import { ResumePaginator, PageMargins } from '@/utils/resumePagination';

interface PaginatedTemplate2Props {
  data: Template1ResumeData;
  margins?: PageMargins;
}

export const PaginatedTemplate2: React.FC<PaginatedTemplate2Props> = ({ data, margins }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paginatedContent, setPaginatedContent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const sections: HTMLElement[] = [];

    // Header Section
    const header = document.createElement('header');
    header.className = 'border-b-2 border-blue-600 pb-3 mb-3';
    header.setAttribute('data-keep-together', 'true');
    header.innerHTML = `
      <h1 class="text-3xl font-bold text-gray-900 mb-1">${data.Name}</h1>
      <p class="text-sm text-gray-600">${data.Email} | ${data.Phone} | ${data.City}, ${data.State}</p>
      ${data.LinkedIn ? `<p class="text-sm text-blue-600">${data.LinkedIn}</p>` : ''}
    `;
    sections.push(header);

    // Summary Section
    const summary = document.createElement('section');
    summary.className = 'mb-3';
    summary.innerHTML = `
      <h2 class="text-lg font-bold text-blue-700 mb-2 border-b border-blue-300 pb-1">PROFESSIONAL SUMMARY</h2>
      <p class="text-xs text-gray-800 leading-snug">${data.Summary}</p>
    `;
    sections.push(summary);

    // Skills Section - Support both SkillRows and Skills formats
    const allSkills = flattenSkills(data);
    if (allSkills.length > 0) {
      const skills = document.createElement('section');
      skills.className = 'mb-3';
      const skillsList = allSkills.join(' • ');
      skills.innerHTML = `
        <h2 class="text-lg font-bold text-blue-700 mb-2 border-b border-blue-300 pb-1">CORE COMPETENCIES</h2>
        <p class="text-xs text-gray-800">${skillsList}</p>
      `;
      sections.push(skills);
    }

    // Career Highlights
    if (data.CareerHighlights && data.CareerHighlights.length > 0) {
      const highlights = document.createElement('section');
      highlights.className = 'mb-3';
      const highlightsHtml = data.CareerHighlights.map(h => `
        <div class="mb-2">
          <p class="text-xs text-gray-800 leading-snug">
            <span class="font-bold text-blue-700">▸ ${h.chtitle}:</span> ${h.chdescription}
          </p>
        </div>
      `).join('');
      highlights.innerHTML = `
        <h2 class="text-lg font-bold text-blue-700 mb-2 border-b border-blue-300 pb-1">KEY ACHIEVEMENTS</h2>
        ${highlightsHtml}
      `;
      sections.push(highlights);
    }

    // Professional Experience
    if (data.ProfessionalExperience && data.ProfessionalExperience.length > 0) {
      const firstJob = data.ProfessionalExperience[0];
      const expBlock = document.createElement('section');
      expBlock.className = 'mb-3';
      expBlock.setAttribute('data-keep-together', 'true');

      const firstBullets = firstJob.Descriptions.map(desc => 
        `<li class="leading-snug text-xs text-gray-800">${desc.Description}</li>`
      ).join('');

      expBlock.innerHTML = `
        <h2 class="text-lg font-bold text-blue-700 mb-2 border-b border-blue-300 pb-1">PROFESSIONAL EXPERIENCE</h2>
        <div class="mb-2">
          <div class="flex justify-between items-baseline">
            <h3 class="font-bold text-sm text-gray-900">${firstJob.company}</h3>
            <span class="text-xs text-gray-600">${firstJob.start_date} - ${firstJob.end_date}</span>
          </div>
          <p class="text-xs italic text-gray-700 mb-1">${firstJob.job_title}${firstJob.location ? ` | ${firstJob.location}` : ''}</p>
          <ul class="list-disc list-inside ml-2 space-y-0.5">${firstBullets}</ul>
        </div>
      `;
      sections.push(expBlock);

      data.ProfessionalExperience.slice(1).forEach((job) => {
        const jobSection = document.createElement('section');
        jobSection.className = 'mb-3';
        jobSection.setAttribute('data-experience-continuation', 'true');
        
        const bullets = job.Descriptions.map(desc => 
          `<li class="leading-snug text-xs text-gray-800">${desc.Description}</li>`
        ).join('');
        
        jobSection.innerHTML = `
          <div class="mb-2">
            <div class="flex justify-between items-baseline" data-stick-with-next-list="true">
              <h3 class="font-bold text-sm text-gray-900">${job.company}</h3>
              <span class="text-xs text-gray-600">${job.start_date} - ${job.end_date}</span>
            </div>
            <p class="text-xs italic text-gray-700 mb-1" data-stick-with-next-list="true">${job.job_title}${job.location ? ` | ${job.location}` : ''}</p>
            <ul class="list-disc list-inside ml-2 space-y-0.5">${bullets}</ul>
          </div>
        `;
        sections.push(jobSection);
      });
    }

    // Education Section
    const education = document.createElement('section');
    education.className = 'mb-3';
    const eduHtml = data.Education.map(edu => `
      <div class="mb-1">
        <p class="text-xs font-semibold text-gray-900">${edu.College_Name}</p>
        <p class="text-xs text-gray-800">${edu.College_Degree_Name}${edu.Major ? `, ${edu.Major}` : ''}${(edu.Year_Earned || edu.YearEarned) ? ` | ${edu.Year_Earned || edu.YearEarned}` : ''}</p>
      </div>
    `).join('');
    education.innerHTML = `
      <h2 class="text-lg font-bold text-blue-700 mb-2 border-b border-blue-300 pb-1">EDUCATION</h2>
      ${eduHtml}
    `;
    sections.push(education);

    // Certifications - Filter out null entries
    const validCertifications = filterNullEntries(data.CertificationsAndLicenses);
    if (validCertifications.length > 0) {
      const certs = document.createElement('section');
      certs.className = 'mb-3';
      const certsHtml = validCertifications.map(cert => `
        <p class="text-xs text-gray-800 mb-1">
          <span class="font-semibold">${cert.Certification_or_License_Name}</span>
          ${cert.Institution_Organization ? ` - ${cert.Institution_Organization}` : ''}
          ${cert.Year ? ` (${cert.Year})` : ''}
        </p>
      `).join('');
      certs.innerHTML = `
        <h2 class="text-lg font-bold text-blue-700 mb-2 border-b border-blue-300 pb-1">CERTIFICATIONS</h2>
        ${certsHtml}
      `;
      sections.push(certs);
    }

    // Awards - Filter out null entries
    const validAwards = filterNullEntries(data.AwardsAndHonors);
    if (validAwards.length > 0) {
      const awards = document.createElement('section');
      awards.className = 'mb-3';
      const awardsHtml = validAwards.map(award => `
        <p class="text-xs text-gray-800 mb-1">
          <span class="font-semibold">${award.Award_Name}</span>
          ${award.Institution_Organization ? ` - ${award.Institution_Organization}` : ''}
          ${award.Year ? ` (${award.Year})` : ''}
        </p>
      `).join('');
      awards.innerHTML = `
        <h2 class="text-lg font-bold text-blue-700 mb-2 border-b border-blue-300 pb-1">AWARDS & HONORS</h2>
        ${awardsHtml}
      `;
      sections.push(awards);
    }

    const paginator = new ResumePaginator({
      margins: margins || { top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }
    });

    const tempContainer = document.createElement('div');
    const paginated = paginator.paginate(tempContainer, sections);
    setPaginatedContent(paginated);

  }, [data, margins]);

  return (
    <div ref={containerRef}>
      {paginatedContent && (
        <div 
          className="font-sans"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
          dangerouslySetInnerHTML={{ __html: paginatedContent.innerHTML }} 
        />
      )}
    </div>
  );
};
